import styled from '@emotion/styled';
import {
  container,
  device,
  sizes,
  typography,
  inViewConfig,
} from '@landsbankinn/ui';
import React, { Children } from 'react';
import { useInView } from 'react-intersection-observer';
import { animated, useSpring } from 'react-spring';

export interface HeroProps {
  title?: React.ReactNode;
  description?: React.ReactNode;
  isAnimated?: boolean;
}

const isEmpty = (value: string | React.ReactNode) => {
  if (Children.count(value) === 0) {
    return true;
  }
  return String(value ?? '').trim() === '';
};

const Host = styled.div`
  position: relative;
  padding-bottom: 1px;

  margin-bottom: 80px;
  color: ${(props) => props.theme.colors.background};

  @media ${device.tablet} {
    padding-top: 40px;
  }
`;

const Container = styled(animated.div)`
  @media ${device.tablet} {
    ${container}
  }
`;

const AspectRatio = styled.div`
  position: relative;
  display: flex;
  flex-flow: column nowrap;

  margin-bottom: -50px;

  @media ${device.tablet} {
    min-height: 483px;
    flex-direction: row;
  }
`;

const Title = styled.h1`
  ${typography.heroSmall}
  max-width: calc((690 / ${sizes.heroSmallSize}) * 1em);

  margin: 0;
  margin-bottom: 20px;

  color: inherit;

  @media ${device.tablet} {
    margin-bottom: calc(${75 / sizes.heroSmallSize} * 1em);
    max-width: 100%;
  }
`;

const Description = styled.div`
  font-family: ${(props) => props.theme.font.family};
  font-size: 24px;
  letter-spacing: 0.38px;
  text-align: left;
  line-height: 34px;
  @media ${device.tablet} {
    margin-top: auto;
    padding-right: 100px;
  }

  div {
    ${typography.p2}

    p {
      margin-bottom: 36px;
    }

    ul li {
      ${typography.heroListStyle}

      &::before {
        background-color: ${(props) => props.theme.colors.secondary};
      }
    }
  }
`;

const Background = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-image: ${(props) => props.theme.gradients.primary};
`;

const Content = styled.div`
  position: relative;
  padding: 60px 30px;

  display: flex;
  flex-direction: column;
  background-color: ${(props) => props.theme.colors.primary};

  @media ${device.tablet} {
    padding: 136px 100px 50px 100px;
    background-color: transparent;
  }
`;

const Copy = styled(animated.div)`
  display: flex;
  flex: 1;
  flex-direction: column;
`;

export function CompactHero(props: HeroProps) {
  const [ref, inView] = useInView(inViewConfig);

  const { isAnimated, title, description } = props;

  const visible = isAnimated ?? inView;

  const copyStyle = useSpring({
    opacity: visible ? (1 as any) : (0 as any),
    y: visible ? 0 : 20,
  });

  return (
    <Host ref={ref} id="hero">
      <Container>
        <AspectRatio>
          <Background />
          <Content>
            <Copy style={copyStyle}>
              {!isEmpty(title) && <Title>{title}</Title>}
              {!isEmpty(description) && (
                <Description>{description}</Description>
              )}
            </Copy>
          </Content>
        </AspectRatio>
      </Container>
    </Host>
  );
}
